/**
 * Stripe Web Payment Component
 * Uses Stripe Embedded Checkout for in-app payment on web
 * Works with Stripe 2025 API
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, ScrollView, Platform, Animated } from 'react-native';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import { subscriptionsApi, PriceKey } from '../../api/endpoints/subscriptions';
import logger from '../../utils/logger';

interface StripeWebPaymentProps {
  onSuccess: () => void;
  onCancel: () => void;
  priceKey?: PriceKey;
}

export default function StripeWebPayment({ onSuccess, onCancel, priceKey = 'premium_monthly' }: StripeWebPaymentProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  // Animation values for success screen
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.8)).current;

  // Handle checkout completion
  const handleComplete = useCallback(() => {
    logger.log('[StripeWebPayment] 🎉 Checkout completed - showing success animation');
    console.log('[StripeWebPayment] 🎉 Checkout completed - showing success animation');
    setIsComplete(true);

    // Clean up localStorage
    localStorage.removeItem('pending_session_id');

    // Animate success screen
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 50,
        friction: 7,
        useNativeDriver: true,
      }),
    ]).start();

    // Delay to show success message before closing
    setTimeout(() => {
      onSuccess();
    }, 3500);
  }, [onSuccess, fadeAnim, scaleAnim]);

  useEffect(() => {
    // Check if returning from redirect (e.g., 3D Secure)
    const handleRedirectReturn = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const returnedSessionId = urlParams.get('session_id');

      if (returnedSessionId) {
        logger.log('[StripeWebPayment] 🎉 Returning from redirect, session_id:', returnedSessionId);
        console.log('[StripeWebPayment] 🎉 Returning from 3D Secure redirect');

        // Clean up URL params
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);

        // Clean up localStorage
        localStorage.removeItem('pending_session_id');

        // Payment was successful - show success animation
        setIsComplete(true);
        setIsLoading(false);

        // Start the animation
        Animated.parallel([
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 400,
            useNativeDriver: true,
          }),
          Animated.spring(scaleAnim, {
            toValue: 1,
            tension: 50,
            friction: 7,
            useNativeDriver: true,
          }),
        ]).start();

        // Delay before closing to show animation
        setTimeout(() => {
          onSuccess();
        }, 3500);
        return;
      }

      // No redirect return, create new checkout session
      await setupPayment();
    };

    // Create checkout session when component mounts
    const setupPayment = async () => {
      try {
        logger.log('[StripeWebPayment] Creating checkout session for:', priceKey);
        const response = await subscriptionsApi.createPaymentIntent(priceKey);

        if (response.success && response.client_secret) {
          if (!response.publishable_key) {
            logger.error('[StripeWebPayment] Missing publishable_key from API response');
            throw new Error('Configuration Stripe manquante. Contactez le support.');
          }

          setClientSecret(response.client_secret);
          setSessionId(response.session_id);

          // Save session_id in case of redirect (3DS)
          localStorage.setItem('pending_session_id', response.session_id);

          // Initialize Stripe with the publishable key from backend
          setStripePromise(loadStripe(response.publishable_key));

          logger.log('[StripeWebPayment] Got client secret and session_id:', response.session_id);
        } else {
          logger.error('[StripeWebPayment] API response:', response);
          throw new Error('Impossible de préparer le paiement');
        }
      } catch (err: any) {
        logger.error('[StripeWebPayment] Setup error:', err);
        setError(err.message || 'Impossible de préparer le paiement');
      } finally {
        setIsLoading(false);
      }
    };

    handleRedirectReturn();
  }, [priceKey, onSuccess]);

  // Show success screen with animation
  if (isComplete) {
    return (
      <View style={styles.successContainer}>
        <Animated.View
          style={[
            styles.successContent,
            {
              opacity: fadeAnim,
              transform: [{ scale: scaleAnim }],
            },
          ]}
        >
          <View style={styles.successIconContainer}>
            <Text style={styles.successIcon}>✓</Text>
          </View>
          <Text style={styles.successTitle}>Bienvenue chez les Premium !</Text>
          <Text style={styles.successMessage}>
            Ton abonnement est maintenant actif.{'\n'}
            Tu as accès à toutes les fonctionnalités.
          </Text>
          <View style={styles.successFeatures}>
            <Text style={styles.featureItem}>✨ Chat illimité avec l'IA</Text>
            <Text style={styles.featureItem}>📊 Diagnostic financier complet</Text>
            <Text style={styles.featureItem}>🎯 Suivi de tes objectifs</Text>
            <Text style={styles.featureItem}>🔔 Alertes personnalisées</Text>
          </View>
          <ActivityIndicator color={colors.primary} style={styles.loader} />
          <Text style={styles.redirectText}>Redirection en cours...</Text>
        </Animated.View>
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>Préparation du paiement...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorTitle}>Erreur</Text>
        <Text style={styles.errorMessage}>{error}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={() => {
          localStorage.removeItem('pending_session_id');
          onCancel();
        }}>
          <Text style={styles.retryButtonText}>Retour</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (!clientSecret || !sessionId || !stripePromise) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <Text style={styles.headerTitle}>Passer à Premium</Text>
        <TouchableOpacity
          style={styles.closeButton}
          onPress={() => {
            localStorage.removeItem('pending_session_id');
            onCancel();
          }}
        >
          <Text style={styles.closeButtonText}>✕</Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={true}
      >
        <View style={styles.checkoutContainer}>
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{
              clientSecret,
              onComplete: handleComplete,
            }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    // Ensure the container fills available space
    ...(Platform.OS === 'web' ? {
      height: '100%',
      minHeight: '100vh',
    } as any : {}),
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
    // Keep header fixed at top on web
    ...(Platform.OS === 'web' ? {
      position: 'sticky',
      top: 0,
      zIndex: 10,
    } as any : {}),
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  closeButton: {
    padding: spacing.sm,
  },
  closeButtonText: {
    fontSize: typography.fontSize.lg,
    color: colors.textSecondary,
  },
  scrollContainer: {
    flex: 1,
    // Enable scrolling on web
    ...(Platform.OS === 'web' ? {
      overflowY: 'auto',
      overflowX: 'hidden',
    } as any : {}),
  },
  scrollContent: {
    flexGrow: 1,
    // Ensure content can expand
    ...(Platform.OS === 'web' ? {
      minHeight: 600,
    } as any : {}),
  },
  checkoutContainer: {
    flex: 1,
    padding: spacing.md,
    // Critical: Set minimum height for iframe to render properly
    ...(Platform.OS === 'web' ? {
      minHeight: 550,
      height: 'auto',
    } as any : {}),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  errorTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.error,
    marginBottom: spacing.sm,
  },
  errorMessage: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  retryButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
  },
  successContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
    backgroundColor: colors.background,
  },
  successContent: {
    alignItems: 'center',
    maxWidth: 320,
  },
  successIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#10B981',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
    shadowColor: '#10B981',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  successIcon: {
    fontSize: 40,
    color: '#FFFFFF',
  },
  successTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  successMessage: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.lg,
  },
  successFeatures: {
    alignSelf: 'stretch',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.lg,
  },
  featureItem: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
    paddingVertical: spacing.xs,
  },
  loader: {
    marginBottom: spacing.sm,
  },
  redirectText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
});
